/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'

import CropUpdateMediaCarousel from './CropUpdateMediaCarousel'

import { mediaJSX } from './helpers/mediaJSX'
import { tagsJSX } from './helpers/tagsJSX'
import { descriptionJSX } from './helpers/descriptionJSX'
import { captionsJSX } from './helpers/captionsJSX'
import { analysisJSX } from './helpers/analysisJSX'

import { navbarHeight, footerHeight, positionChangeHeightSm, positionChangeWidthSm, positionChangeWidthMd } from '../../../helpers/variableDefaults'
import { getPayload, userIsAuthenticated, getTokenFromLocalStorage } from '../../../helpers/storage'
import { descriptionTextField } from '../../../helpers/textfields'
import { standardButton } from '../../../helpers/buttons'
import { standardSpinner } from '../../../helpers/spinners'
import useWindowDimensions from '../../../helpers/windowDimensions'
import { Input } from '../../../helpers/globalHelpers'
import { returnDurationFromLiveURL, returnVideoThumbnailFromLiveURL } from '../../../helpers/imageHandling'
import { handleSetVideoThumbnailMediaItem } from '../../../helpers/commonMethods'

import { seoPageTags, customAnalyticsEvent } from '../../../helpers/analytics'

// MediaItem Page
const MediaItem = () => {
  
  // Window Dimensions
  const { height, width } = useWindowDimensions()
  
  // Navigate
  const navigate = useNavigate()

  // Params
  const { mediaItemId, userId } = useParams()

  // Payload
  const payload = getPayload()

  // Media Item
  // const [user, setUser] = useState({})
  const [mediaItem, setMediaItem] = useState({})
  const [updatedMediaItemObj, setUpdatedMediaItemObj] = useState({}) // Used for updating the image/video
  const [previousMediaItem, setPreviousMediaItem] = useState({})
  const [tagToAdd, setTagToAdd] = useState('')
  const [proToAdd, setProToAdd] = useState('')
  const [conToAdd, setConToAdd] = useState('')

  // Video Handling
  const [videoDuration, setVideoDuration] = useState(-1)
  const [videoThumbnail, setVideoThumbnail] = useState('')

  // Loading
  const [loading, setLoading] = useState({
    loading: false,
    image: false,
    tags: false,
    description: false,
    captions: false,
    analysis: false,
  })
  const [videoIsLoadingSquare, setVideoIsLoadingSquare] = useState(false)
  const [videoIsLoadingPortrait, setVideoIsLoadingPortrait] = useState(false)

  // Save Successful
  const [saveSuccessful, setSaveSuccessful] = useState({
    image: false,
    tags: false,
    description: false,
    captions: false,
    analysis: false,
  })
  
  // Errors
  const [errors, setErrors] = useState({
    errors: false,
    image: false,
    tags: false,
    description: false,
    captions: false,
    analysis: false,
  })

  const resetLoadingAndErrors = () => {
    setLoading({
      loading: false,
      image: false,
      tags: false,
      description: false,
      captions: false,
      analysis: false,
    })

    setErrors({
      errors: false,
      image: false,
      tags: false,
      description: false,
      captions: false,
      analysis: false,
    })
  }

  const resetSaveSuccessful = () => {
    setSaveSuccessful({
      image: false,
      tags: false,
      description: false,
      captions: false,
      analysis: false,
    })
  }

  const isLoading = () => {
    return Object.keys(loading).filter(loadingKey => loading[loadingKey] === true).length > 0
  }

  // Use Effect — Initial load to Retrieve Media Item
  useEffect(() => {

    if (!userIsAuthenticated()) {
      console.log('user not authenticated')
      navigate('/login')
    } else if (userId !== payload.sub) {
      console.log('different userIds')
      navigate(`/dashboard/${payload.sub}`)
      // reloadView()
    } else {
      const getData = async () => {
        setLoading({ ...loading, loading: true })
        try {
          const { data: retrievedMediaItem } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/media-items/${mediaItemId}`, {
            headers: {
              Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            },
          })
          // console.log('retrieved Media Item ->', retrievedMediaItem)

          if (payload.sub === retrievedMediaItem.userId) {
            // Set States
            setMediaItem({ ...retrievedMediaItem })
            setPreviousMediaItem({ ...retrievedMediaItem })
  
            if (retrievedMediaItem.type === 'video') {
              await handleSetVideoThumbnailMediaItem(retrievedMediaItem, setVideoThumbnail, setVideoDuration)
            }
            
            setErrors({ ...errors, errors: false })
            setLoading({ ...loading, loading: false })
          } else {
            navigate(`/dashboard/${payload.sub}`)
            setErrors({ ...errors, errors: true })
            setLoading({ ...loading, loading: false })
          }

        } catch (error) {
          // console.log(error)

          navigate(`/dashboard/${payload.sub}`)

          setErrors({ ...errors, errors: true })
          setLoading({ ...loading, loading: false })
        }
      }

      getData()
    }

  }, [])


  useEffect(() => {
    // console.log('useEffect Runs')
    // console.log('videoThumbnail ->', videoThumbnail)

  }, [mediaItem, videoThumbnail, saveSuccessful, errors])

  const handleDeleteMediaItem = async () => {
    // console.log('handleDeleteMediaItem runs')
    resetLoadingAndErrors()

    try {
      setLoading({ ...loading, loading: true })
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/media-items/${mediaItemId}`, {
        headers: {
          Authorization: `Bearer ${getTokenFromLocalStorage()}`,
        },
      })

      navigate(`/dashboard/${payload.sub}`, { state: { previousLocationPathname: window.location.pathname } })

      setErrors({ ...errors, errors: false })
      setLoading({ ...loading, loading: false })
    } catch (error) {
      console.log('error deleting item ->', error)
      setLoading({ ...loading, loading: false })
      setErrors({ ...errors, errors: true })
    }
  }

  const handleBackToDashboardPressed = () => {
    // console.log('handleBackToDashboardPressed runs')
    navigate(`/dashboard/${payload.sub}`, { state: { previousLocationPathname: window.location.pathname } })
  }

  return (
    <>
      {/* Helmet — for analytics, seo, and page title changing */}
      {seoPageTags(
        'Media Item'
      )}

      {/* Body */}

      {loading.loading ?
        <Box sx={{ 
          width: '100vw', minWidth: '100vw',
          // minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight})`,
          minHeight: `calc(100vh - ${navbarHeight})`,
          mt: 0, mb: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        }}>
          {standardSpinner()}
        </Box>
        :
        Object.keys(updatedMediaItemObj).length > 0 ?
          <CropUpdateMediaCarousel updatedMediaItemObj={updatedMediaItemObj} setUpdatedMediaItemObj={setUpdatedMediaItemObj} mediaItem={mediaItem} setMediaItem={setMediaItem} previousMediaItem={previousMediaItem} setPreviousMediaItem={setPreviousMediaItem} resetLoadingAndErrors={resetLoadingAndErrors} width={width} />
          :
          <Box 
            sx={{ 
              my: 5,
              minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight})`, 
              width: '100vw', 
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', 
            }}
          >
            {/* Back to Dashboard Button */}
            <Box
              sx={{
                mb: 2,
                width: '100%',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              }}
            >
              {userId && 
                standardButton(
                  'Back to Dashboard', 
                  'button',
                  'text',
                  isLoading(),
                  'secondary', 
                  width >= positionChangeWidthSm ? 1 : 1,
                  0,
                  0,
                  '250px', 
                  '45px', 
                  handleBackToDashboardPressed
                )
              }
            </Box>

            {/* Image/Video */}
            {Object.keys(mediaItem).length > 0 
              && mediaJSX(
                mediaItem, setMediaItem,
                updatedMediaItemObj, setUpdatedMediaItemObj,
                previousMediaItem, setPreviousMediaItem,
                videoThumbnail, videoDuration,
                videoIsLoadingSquare, setVideoIsLoadingSquare,
                videoIsLoadingPortrait, setVideoIsLoadingPortrait,
                loading, setLoading, 
                saveSuccessful, setSaveSuccessful,
                errors, setErrors,
                resetLoadingAndErrors, resetSaveSuccessful,
                width
              )
            }


            {/* Captions */}
            {Object.keys(mediaItem).length > 0 
              && captionsJSX(
                mediaItem, setMediaItem,
                previousMediaItem, setPreviousMediaItem,
                loading, setLoading, 
                saveSuccessful, setSaveSuccessful,
                errors, setErrors,
                resetLoadingAndErrors, resetSaveSuccessful,
                width
              )
            }


            {/* Description */}
            {/* {Object.keys(mediaItem).length > 0 
              && descriptionJSX(
                mediaItem, setMediaItem,
                previousMediaItem, setPreviousMediaItem,
                loading, setLoading, 
                saveSuccessful, setSaveSuccessful,
                errors, setErrors,
                resetLoadingAndErrors, resetSaveSuccessful,
                width
              )
            } */}


            {/* Tags */}
            {/* {Object.keys(mediaItem).length > 0 
              && tagsJSX(
                mediaItem, setMediaItem,
                previousMediaItem, setPreviousMediaItem,
                tagToAdd, setTagToAdd,
                loading, setLoading, 
                saveSuccessful, setSaveSuccessful,
                errors, setErrors,
                resetLoadingAndErrors, resetSaveSuccessful,
                width
              )
            } */}


            {/* Analysis */}
            {/* {Object.keys(mediaItem).length > 0 
              && analysisJSX(
                mediaItem, setMediaItem,
                previousMediaItem, setPreviousMediaItem,
                proToAdd, setProToAdd,
                conToAdd, setConToAdd,
                loading, setLoading, 
                saveSuccessful, setSaveSuccessful,
                errors, setErrors,
                resetLoadingAndErrors, resetSaveSuccessful,
                width
              )
            } */}


            {/* Delete */}
            <Box
              sx={{
                py: 3, my: 2,
                width: '90%',
                maxWidth: '600px',
                backgroundColor: 'whitesmoke',
                borderRadius: 3, boxShadow: 3,
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: '90%',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
                }}
              >
                {standardButton(
                  'Delete Media Item', 
                  'button',
                  'contained',
                  isLoading(),
                  'error', 
                  1,
                  1,
                  0,
                  '180px',
                  '45px',
                  handleDeleteMediaItem
                )}
              </Box>
            </Box>
          </Box>
      }
    </>
  )
}

export default MediaItem